import { FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { authApi } from '../api/authApi';
import { useForcedPasswordChange } from '../hooks/useForcedPasswordChange';
import { usePageTitle } from '../hooks/usePageTitle';

export function ForcedChangePasswordPage() {
  const navigate = useNavigate();
  const { onPasswordChanged } = useForcedPasswordChange();

  // WCAG 2.4.2 (Page Titled, Level A). Unlike the other pages that were missing a title,
  // this one is NOT admin-only — any user whose account is flagged for a forced change
  // lands here before they can reach anything else, so it sits in the public scope that
  // constitution VI binds.
  usePageTitle('Change Your Password — Hawkes Bridge');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await authApi.changeForcedPassword(newPassword);
      onPasswordChanged();
      navigate('/');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 400) {
        const body = err.response.data as { code?: string; message?: string };
        if (body.code === 'TEMP_PASSWORD_REUSE') {
          setError('Please choose a password different from the one provided by your administrator.');
        } else {
          setError(body.message ?? 'Validation failed.');
        }
      } else {
        setError('Unexpected error changing your password.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
      <h1 className="text-2xl font-bold text-ink mb-2">Set your password</h1>
      <p className="text-sm text-ink mb-6">
        You're using a temporary password set by an administrator. Please choose a new
        password to continue.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4 bg-card rounded-lg shadow p-6 border border-line">
        <div>
          <label htmlFor="newPassword" className="block text-sm font-medium text-ink">New password</label>
          <input
            id="newPassword"
            type="password"
            required
            minLength={8}
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium text-ink">Confirm password</label>
          <input
            id="confirmPassword"
            type="password"
            required
            minLength={8}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            className="mt-1 block w-full rounded-md border-line-strong shadow-sm focus:border-primary-500 focus:ring-primary-500"
          />
        </div>

        {error && (
          <div role="alert" className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-60"
        >
          {submitting ? 'Saving…' : 'Set password and continue'}
        </button>
      </form>
    </div>
  );
}
